<template>
  <v-container grid-list-lg fluid>
    <TitleBar :title="$t('pageHeader.updateEmployee')" />
    <v-card flat class="outlined" style="border-radius: 15px">
      <CardToolbar :title="$t('labels.employee')" :isModal="false" />
      <v-card-text>
        <v-layout row wrap>
          <v-flex xs12 sm6>
            <span class="mb-1 font-weight-regular">{{ $t('labels.firstName') }}</span>
            <v-text-field type="text" solo v-model="employee.firstName" @focus="$event.target.select()"
              :error-messages="firstNameErrors" :label="$t('labels.firstName')"></v-text-field>
          </v-flex>

          <v-flex xs12 sm6>
            <span class="mb-1 font-weight-regular">{{ $t('labels.lastName') }}</span>
            <v-text-field type="text" solo v-model="employee.lastName" @focus="$event.target.select()"
              :label="$t('labels.lastName')"></v-text-field>
          </v-flex>

          <v-flex xs12 sm6>
            <span class="mb-1 font-weight-regular">{{ $t('labels.pinNumber') }}</span>
            <v-text-field v-model="employee.pinCode" solo :append-icon="showPassword ? 'visibility_off' : 'visibility'"
              :type="showPassword ? 'text' : 'password'" :label="$t('labels.pinCode')" @focus="$event.target.select()"
              v-on:keyup.enter="submit" :error-messages="pinErrors" counter
              @click:append="showPassword = !showPassword"></v-text-field>
          </v-flex>

          <v-flex xs12 sm6>
            <span class="mb-1 font-weight-regular">{{ $t('labels.phoneNumber') }}</span>
            <v-text-field type="tel" solo v-model="employee.phoneNumber" :error-messages="phoneNumberErrors"
              :label="$t('labels.phoneNumber')" @focus="$event.target.select()" mask="phone"></v-text-field>
          </v-flex>
        </v-layout>

        <v-layout row wrap v-if="checkTimeClockModule">
          <v-flex xs12 sm12>
            <span class="mb-1 font-weight-regular">{{ $t('labels.hourlyRate') }}</span>
            <v-text-field type="number" solo v-model="employee.hourlyRate"
              :label="$t('labels.hourlyRate')"></v-text-field>
          </v-flex>
          <v-flex xs12 sm6>
            <span class="mb-1 font-weight-regular">{{
              $t('labels.expectedWorkHours')
              }}</span>
            <v-text-field return-masked-value mask="##:##" solo v-model="employee.metadata.employeeHours.workingHours"
              :label="$t('labels.workingHours')"></v-text-field>
          </v-flex>

          <!-- <v-flex xs12 sm6>
            <span class="mb-1 font-weight-regular">{{ $t('labels.breakHours') }}</span>
            <v-text-field
              type="number"
              suffix="minutes"
              solo
              v-model="employee.metadata.employeeHours.breakHours"
              :label="$t('labels.breakHours')"
            ></v-text-field>
          </v-flex> -->
        </v-layout>
        <v-layout row wrap>
          <v-flex xs12 sm6>
            <v-layout row wrap>
              <v-flex>
                <v-card class="outlined" flat>
                  <v-card-text>
                    <span class="font-weight-medium">Type</span>
                    <v-layout column>
                      <v-flex>
                        <v-checkbox hide-details v-model="employee.metadata.isDriver"
                          v-if="locationModules.terminalordering" label="Driver"></v-checkbox>
                        <v-checkbox hide-details v-if="
                          locationModules.terminalrepairs ||
                          locationModules.terminalautoshop
                        " v-model="employee.metadata.isTechnician" label="Technician"></v-checkbox>
                      </v-flex>
                    </v-layout>
                  </v-card-text>
                </v-card>
              </v-flex>
              <v-flex v-if="checkTimeClockModule">
                <v-card class="outlined" flat>
                  <v-card-text>
                    <span class="font-weight-medium">{{ $t('labels.timeClockLogin') }}</span>
                    <v-layout column>
                      <v-flex class="pa-0">
                        <v-checkbox hide-details v-model="employee.metadata.timeClockLogin.admin"
                          label="Admin"></v-checkbox>
                        <v-checkbox hide-details v-model="employee.metadata.timeClockLogin.employee"
                          label="Employee"></v-checkbox>
                      </v-flex>
                    </v-layout>
                  </v-card-text>
                </v-card>
              </v-flex>
            </v-layout>
          </v-flex>

          <v-flex xs12 sm6>
            <span class="mb-1 font-weight-regular">{{ $t('labels.emailAddress') }}</span>
            <v-text-field type="email" solo v-model="employee.email" :error-messages="emailErrors"
              :label="$t('labels.emailAddress')"></v-text-field>
          </v-flex>

          <v-flex grow>
            <v-textarea name="input-7-1" :label="$t('labels.note')" auto-grow solo v-model="employee.note"></v-textarea>
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-card-text>
        <v-layout row wrap align-center>
          <v-flex grow xs12 md9 lg10>
            <span class="font-weight-medium">Hide Payee</span>
            <p class="font-weight-light">
              Hide selected payee to from {{ employee.firstName }} {{ employee.lastName }}.
            </p>
          </v-flex>
          <v-flex shrink xs12 md3 lg2>
            <v-switch :loading="payeeLoading" v-model="hidePayee" color="teal"
              :label="hidePayee ? 'Enabled' : 'Disabled'"></v-switch>
          </v-flex>
        </v-layout>
        <v-card flat class="outlined" v-if="hidePayee">
          <v-card-text>
            <v-layout align-center row class="mb-3">
              <v-flex> Payee List </v-flex>
              <v-spacer></v-spacer>

              <v-flex align-self-center xs12 sm4>
                <v-text-field v-model="search" class="rounded" prepend-inner-icon="search" data-cy="searchBar"
                  label="Search" :append-icon="`${search ? 'close' : ''}`" @click:append="setNull()" single-line solo
                  hide-details />
              </v-flex>
            </v-layout>
            <v-data-table v-model="selected" :headers="headers" :items="Payees" :search="search"
              :pagination.sync="pagination" item-key="id" class="elevation-0 table-header">
              <template v-slot:headers="props">
                <tr>
                  <th width="35">
                    <v-checkbox :input-value="props.all" :indeterminate="props.indeterminate" primary hide-details
                      @click.stop="toggleAll"></v-checkbox>
                  </th>
                  <th v-for="header in props.headers" :key="header.text">
                    {{ header.text }}
                  </th>
                </tr>
              </template>
              <template v-slot:items="props">
                <tr :active="props.selected" @click="props.selected = !props.selected">
                  <td>
                    <v-checkbox :input-value="props.selected" primary hide-details></v-checkbox>
                  </td>
                  <td>{{ props.item.name }}</td>
                  <td data-cy="expenseModulePayeeListGridPhoneNumber" class="text-xs-left">
                    {{ props.item.phoneNumber }}
                  </td>
                  <td data-cy="expenseModulePayeeListGridEmail" class="text-xs-left">
                    {{ props.item.email }}
                  </td>
                  <td class="text-xs-left">
                    {{ props.item.accountNumber ? props.item.accountNumber : 'N/A' }}
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-card-text>

      <v-card-text>
        <v-expansion-panel class="elevation-0">
          <v-expansion-panel-content>
            <div slot="header">
              <span class="headline">{{ $t('pageHeader.address') }}</span>
            </div>
            <v-card flat class="outlined" style="border-radius: 15px">
              <v-container grid-list-lg>
                <v-layout row wrap>
                  <v-flex xs12 sm6>
                    <v-text-field type="text" solo v-model="employee.Address.type" @focus="$event.target.select()"
                      :label="$t('labels.type')" hint="ie. Home, Office, Business"></v-text-field>
                  </v-flex>

                  <v-flex xs12 sm6>
                    <v-text-field type="text" solo v-model="employee.Address.phoneNumber"
                      @focus="$event.target.select()" :label="$t('labels.phoneNumber')"></v-text-field>
                  </v-flex>

                  <v-flex xs12 sm6>
                    <v-text-field type="text" solo v-model="employee.Address.addressLine1"
                      :error-messages="addressLine1Errors" :label="$t('labels.address')"
                      @focus="$event.target.select()"></v-text-field>
                  </v-flex>

                  <v-flex xs12 sm6>
                    <v-text-field type="text" solo v-model="employee.Address.addressLine2"
                      @focus="$event.target.select()" :label="$t('labels.addressLine2')"></v-text-field>
                  </v-flex>

                  <v-flex xs12 sm6>
                    <v-text-field type="text" solo v-model="employee.Address.city" :error-messages="cityErrors"
                      :label="$t('labels.city')" @focus="$event.target.select()"></v-text-field>
                  </v-flex>

                  <v-flex xs12 sm6>
                    <v-autocomplete :items="states" solo :label="$t('labels.state')" item-text="name" item-value="id"
                      v-model="employee.Address.StateId"></v-autocomplete>
                  </v-flex>

                  <v-flex xs12 sm6>
                    <v-text-field type="text" solo v-model="employee.Address.postalCode"
                      :label="$t('labels.zipCode')"></v-text-field>
                  </v-flex>

                  <v-flex xs12 sm6>
                    <v-autocomplete :items="countries" solo :label="$t('labels.country')" item-text="name"
                      item-value="code" v-model="employee.Address.CountryCode"></v-autocomplete>
                  </v-flex>

                  <v-flex xs12>
                    <v-textarea name="input-7-1" solo :label="$t('labels.description')" auto-grow
                      v-model="employee.Address.description"></v-textarea>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-card>
          </v-expansion-panel-content>
          <v-expansion-panel-content>
            <div slot="header">
              <span class="headline">{{ $t('labels.locations') }}</span>
            </div>
            <v-card flat class="outlined" style="border-radius: 15px">
              <v-card-text>
                <v-layout row wrap>
                  <v-flex>
                    <v-autocomplete solo :label="$t('labels.selectLocation')" :items="locations" item-text="name"
                      v-model="selectedLocation" return-object>
                      <template slot="item" slot-scope="{ item }">
                        <span>{{ item.name }}</span>
                        <v-spacer />
                        <span v-if="item.Address">{{ item.Address.addressLine1 }}</span>
                      </template>
                    </v-autocomplete>
                  </v-flex>
                  <v-flex>
                    <v-autocomplete :label="$t('labels.selectRole')" :items="roles" solo item-text="name"
                      v-model="selectedRole" return-object>
                      <template slot="item" slot-scope="{ item }">
                        <span>{{ item.name }}</span>
                      </template>
                    </v-autocomplete>
                  </v-flex>
                  <v-flex shrink>
                    <v-btn class="mt-0" color="teal" round :disabled="!selectedLocation || !selectedRole"
                      @click="addLocation">Add Location</v-btn>
                  </v-flex>
                </v-layout>
                <v-data-table :headers="locationTable.headers" :items="employee.ActiveLocations" hide-actions>
                  <template v-if="!(props.item.delete === true)" slot="items" slot-scope="props">
                    <td class="text-xs-left">
                      {{ props.item.Location.name }}
                    </td>
                    <td class="text-xs-left">
                      {{
                        props.item.EmployeeRole ? props.item.EmployeeRole.name : 'No Role'
                      }}
                    </td>
                    <td class="justify-center layout px-0">
                      <v-icon @click="deleteLocation(props.index)"> delete </v-icon>
                    </td>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-card-text>

      <v-card-actions :style="{ 'background-color': $style.card.action }">
        <v-spacer></v-spacer>
        <v-btn color="info" flat @click="cancel">{{ $t('buttons.cancel') }}</v-btn>
        <v-btn color="purple" dark @click="submit">{{ $t('buttons.update') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import payeeService from '@/modules/Payee/service.js'
import employeeService from '../service'
import locationService from '../../Location/service.js'
import rolesService from '../modules/Roles/service.js'

const { validationMixin } = require('vuelidate')
const { required, minLength, email, requiredIf } = require('vuelidate/lib/validators')

export default {
  name: 'employee-edit',
  props: {
    isModal: Boolean,
  },
  data() {
    return {
      search: '',
      pagination: {
        sortBy: 'name',
      },
      selected: [],
      headers: [
        {
          text: 'Name',
          align: 'left',
          value: 'name',
        },
        {
          text: 'Phone Number',
          align: 'left',
          value: 'phoneNumber',
        },
        {
          text: 'Email',
          align: 'left',
          value: 'email',
        },
        {
          text: 'Account',
          align: 'left',
          value: 'accountNumber',
        },
      ],
      payeeLoading: false,
      Payees: [],
      hidePayee: false,
      locationId: null,
      showPassword: false,
      locations: [],
      roles: [],
      selectedLocation: null,
      selectedRole: null,
      locationTable: {
        headers: [
          {
            text: 'Name',
            value: 'name',
            sortable: false,
          },
          {
            text: 'Role',
            value: 'EmployeeRole',
            sortable: false,
          },
          {
            text: 'Action',
            value: 'action',
            sortable: false,
            align: 'center',
          },
        ],
      },
      employee: {
        firstName: null,
        lastName: null,
        phoneNumber: null,
        accountNumber: null,
        pinCode: null,
        confirmPin: null,
        hourlyRate: null,
        email: null,
        website: null,
        note: null,
        Address: {},
        ActiveLocations: [],
        metadata: {
          employeeHours: {
            breakHours: null,
            workingHours: null,
          },
          hidePayee: {
            list: [],
            isEnable: false,
          },
          timeClockLogin: {
            admin: false,
            employee: false,
          },
        },
      },
    }
  },
  computed: {
    ...mapGetters('global', ['states', 'countries', 'defaultCountryCode', 'location']),
    ...mapGetters('integration', ['locationModules', 'modules']),
    checkTimeClockModule() {
      if (this.locationModules && this.locationModules.terminaltimeclock && this.locationModules.terminaltimeclock.LocationIntegrations.length) {
        return true
      }
      return false
    },
    Address() {
      return this.employee.Address || {}
    },
    firstNameErrors() {
      const errors = []
      if (!this.$v.employee.firstName.$dirty) return errors
      if (!this.$v.employee.firstName.required) {
        errors.push(this.$t('validations.fieldIsRequired'))
      }
      if (!this.$v.employee.firstName.minLength) {
        errors.push(this.$t('validations.fieldMustbeatLeast2ChrLong'))
      }
      return errors
    },
    lastNameErrors() {
      const errors = []
      if (!this.$v.employee.lastName.$dirty) return errors
      if (!this.$v.employee.lastName.minLength) {
        errors.push(this.$t('validations.fieldMustbeatLeast2ChrLong'))
      }
      return errors
    },
    phoneNumberErrors() {
      const errors = []
      if (!this.$v.employee.phoneNumber.$dirty) return errors
      if (!this.$v.employee.phoneNumber.minLength) {
        errors.push(this.$t('validations.validLetters10'))
      }
      return errors
    },
    emailErrors() {
      const errors = []
      if (!this.$v.employee.email.$dirty) return errors
      if (!this.$v.employee.email.email) {
        errors.push(this.$t('validations.emailIsNotValid'))
      }
      return errors
    },
    addressLine1Errors() {
      const errors = []
      if (!this.$v.employee.Address.addressLine1.$dirty) return errors
      if (!this.$v.employee.Address.addressLine1.minLength) {
        errors.push(this.$t('validations.addressMustHaveAtleast2Characters'))
      }
      if (!this.$v.employee.Address.addressLine1.required) {
        errors.push(this.$t('validations.fieldIsRequired'))
      }
      return errors
    },
    cityErrors() {
      const errors = []
      if (!this.$v.employee.Address.city.$dirty) return errors
      if (!this.$v.employee.Address.city.required) {
        errors.push(this.$t('validations.cityIsRequired'))
      }
      return errors
    },
    pinErrors() {
      const errors = []
      if (!this.$v.employee.pinCode.$dirty) return errors
      if (!this.$v.employee.pinCode.required) {
        errors.push(this.$t('validations.fieldIsRequired'))
      }
      if (!this.$v.employee.pinCode.minLength) {
        errors.push(this.$t('validations.fieldMustbeatLeast4ChrLong'))
      }
      return errors
    },
    // confirmPinErrors() {
    //   const errors = []
    //   if (!this.$v.employee.confirmPin.$dirty) return errors
    //   if (!this.$v.employee.confirmPin.sameAsPin)
    //     errors.push(this.$t('validations.pinDoesNotMatch'))
    //   return errors
    // },
  },
  watch: {
    hidePayee(val) {
      console.log('watch got call', val)
      this.employee.metadata.hidePayee.isEnable = val
      if (val) {
        this.getPayee()
      }
    },
    Address() {
      if (Object.keys(this.employee.Address).length > 0) {
        if (!this.employee.Address.city) {
          this.employee.Address.city = this.location.Address.city
        }
        if (!this.employee.Address.StateId) {
          this.employee.Address.StateId = this.location.Address.StateId
        }
        if (!this.employee.Address.CountryCode) {
          this.employee.Address.CountryCode = this.location.Address.CountryCode
        }
      }
    },
  },
  async created() {
    try {
      this.roles = (await rolesService.getAll()).data
      this.locations = (await locationService.getAll({ limit: 40 })).data

      if (this.$route.params && this.$route.params.LocationId) {
        this.locationId = this.$route.params.LocationId
      }

      await this.loademployee()
    } catch (error) {
      console.log(error)
    }
  },
  methods: {
    toggleAll() {
      if (this.selected.length) this.selected = []
      else this.selected = this.Payees.slice()
    },
    getPayee() {
      this.payeeLoading = true
      return payeeService
        .getAll({ limit: 500 })
        .then((response) => {
          console.log('response', response)
          this.Payees = response.data
          this.payeeLoading = false
          if (
            this.employee.metadata.hidePayee &&
            this.employee.metadata.hidePayee.isEnable
          ) {
            if (this.employee.metadata.hidePayee.list.length > 0) {
              this.employee.metadata.hidePayee.list.map((row) => {
                this.selected.push(this.Payees.find((payee) => payee.id === row))
              })
              // const found = array1.find(element => element > 10);
            }
          }
        })
        .catch((error) => {
          console.log('error', error)
          this.payeeLoading = false
          this.$swal('Payee', "Can't get payee list", 'error')
        })
    },
    loademployee() {
      if (
        !this.$route.params ||
        (this.$route.params &&
          this.$route.params.Employee &&
          !this.$route.params.Employee.id)
      ) {
        return this.$router.go(-1)
      }
      const { id } = this.$route.params.Employee
      this.$Progress.start()
      employeeService.getById(id, { includeLocations: true }).then((response) => {
        this.$Progress.finish()
        this.employee.id = response.data.id
        this.employee.firstName = response.data.firstName
        this.employee.lastName = response.data.lastName
        this.employee.phoneNumber = response.data.phoneNumber
        this.employee.accountNumber = response.data.accountNumber
        this.employee.email = response.data.email
        this.employee.website = response.data.website
        this.employee.note = response.data.note
        this.employee.hourlyRate = response.data.hourlyRate
        this.employee.pinCode = response.data.pinCode
        if (response.data.metadata) {
          this.employee.metadata = response.data.metadata
          if (!this.employee.metadata.employeeHours) {
            this.employee.metadata.employeeHours = {
              breakHours: null,
              workingHours: null,
            }
          }
          if (!this.employee.metadata.timeClockLogin) {
            this.employee.metadata.timeClockLogin = {
              admin: false,
              employee: false,
            }
          }
          if (!this.employee.metadata.hidePayee) {
            this.employee.metadata.hidePayee = {
              list: [],
              isEnable: false,
            }
          } else {
            this.hidePayee = this.employee.metadata.hidePayee.isEnable
          }
        }
        Promise.all(
          response.data.ActiveLocations.map(async (location) => {
            this.selectedLocation = location
            await this.addLocation()
            return true
          }),
        )
        // todo: support more than one address
        this.employee.Address = response.data.Address || {}
      })
    },
    submit() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: 'top-end',
            timer: 2000,
          })
          .fire({
            icon: 'error',
            title: this.$t('toastMessage.text.invalidFields'),
          })
      } else {
        this.$Progress.start()

        this.employee.metadata.hidePayee.list = []
        if (this.selected.length > 0) {
          this.selected.map((row) => {
            this.employee.metadata.hidePayee.list.push(row.id)
          })
        }

        employeeService
          .update(this.employee.id, this.employee)
          .then((response) => {
            this.$swal
              .mixin({
                toast: true,
                position: 'top-end',
                timer: 2000,
              })
              .fire({
                icon: 'success',
                title: this.$t('toastMessage.text.employeeUpdate'),
              })
            this.$Progress.finish()
            this.close()
          })
          .catch((err) => {
            console.log(err)
            this.$swal
              .mixin({
                toast: true,
                showConfirmButton: true,
                position: 'top-end',
                timer: 3000,
              })
              .fire({
                icon: 'error',
                title: err.data ? err.data.message : err.message,
              })
            this.$Progress.fail()
          })
      }
    },
    addLocation() {
      if (!this.selectedLocation.Location) {
        this.selectedLocation = {
          Location: this.selectedLocation,
          EmployeeRole: this.selectedRole,
        }
      }

      if (this.selectedLocation.delete) {
        this.selectedLocation.delete = false
      } else {
        this.employee.ActiveLocations = this.employee.ActiveLocations.concat(
          this.selectedLocation,
        )
      }
      // test
      // find the select location and remove it from the list
      const foundIndex = this.locations.findIndex((element) =>
        !this.selectedLocation.Location
          ? this.selectedLocation.id === element.id
          : this.selectedLocation.Location.id === element.id,
      )
      if (foundIndex > -1) {
        this.locations.splice(foundIndex, 1)
        this.locations = [...this.locations]
      }

      this.selectedLocation = 0
      this.selectedRole = 0
    },
    deleteLocation(index) {
      console.log(index)
      this.locations = [this.employee.ActiveLocations[index].Location, ...this.locations]
      this.employee.ActiveLocations[index].delete = true
      // Need to refresh the list
      this.employee.ActiveLocations = [...this.employee.ActiveLocations]
    },
    cancel() {
      this.close()
    },
    close() {
      if (!this.isModal) {
        this.$router.go(-1)
      } else {
        this.$emit('close')
      }
    },
  },
  mixins: [validationMixin],
  validations: {
    employee: {
      pinCode: {
        required,
        minLength: minLength(4),
      },
      firstName: {
        required,
        minLength: minLength(2),
      },
      phoneNumber: {
        minLength: minLength(10),
      },
      email: {
        email,
      },
      Address: {
        addressLine1: {
          required: requiredIf((nestedModel) => Object.keys(nestedModel).length > 0),
          minLength: minLength(2),
        },
        city: {
          required: requiredIf((nestedModel) => Object.keys(nestedModel).length > 0),
        },
      },
    },
  },
}
</script>
